import React from 'react';

// ─── Shared patient-board view model + config ────────────────────────────────
// A normalized, presentational shape that both the Coordinate (discharge) and
// Predict features map their domain patients into, so the list + detail UI can
// be a single shared, config-driven implementation.

export interface PhaseDef {
  key: string;
  label: string;
  bgcolor: string;
  color: string;
  interactive?: boolean; // default true; non-interactive phases can't be expanded
}

export interface ScoreCellVM {
  label: string; // disposition / program label
  score: number; // 0-100, color-graded by getScoreStyles
}

export interface UrgencyVM {
  label: string;
  bgcolor: string;
  color: string;
  borderColor: string;
}

export interface PatientCardVM {
  id: string;
  name: string;
  age: number;
  dob: string; // display string
  mrn: string;

  scoreCell: ScoreCellVM;

  locationPrimary: string;   // discharge: "Floor 3 · Rm 12"; predict: department
  locationSecondary: string; // facility name
  physician: string;
  diagnosis: string;
  expectedDischarge: string; // formatted display
  expectedDischargeMs: number; // sortable
  currentLOS: number;

  phaseKey: string;
  urgency?: UrgencyVM; // omitted entirely when the feature has no urgency concept

  assignedToName: string | null;
  assignedToInitials: string | null;

  // Detail-header info lines (each feature supplies its own already-styled rows).
  headerInfoLines: React.ReactNode[];

  // Optional extras used by split/board views.
  admitDate?: string;
  losTargetDays?: number;
  insuranceLine?: string;
  attending?: string;
}

export type OwnershipFilter = 'ALL' | 'MY' | 'UNASSIGNED';

export interface PatientBoardConfig {
  routeBase: string; // e.g. '/discharge' or '/predict'
  title: (ownership: OwnershipFilter) => string;
  phases: PhaseDef[];
  showUrgency: boolean;
  scoreColumnLabel: string; // e.g. 'Predicted Disposition' or 'Score'
  onTake: (id: string) => void;
  // Lowercased, concatenated text used by the quick-filter search box.
  searchText: (vm: PatientCardVM) => string;
}

export const phaseStyle = (config: PatientBoardConfig, key: string): PhaseDef =>
  config.phases.find(p => p.key === key) ?? { key, label: key, bgcolor: '#eceff1', color: '#37474f' };

export const phaseIndex = (config: PatientBoardConfig, key: string): number =>
  config.phases.findIndex(p => p.key === key);
